/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { SubMenu } from '@material/web/menu/internal/submenu/sub-menu.js';
import { styles } from '@material/web/menu/internal/submenu/sub-menu-styles.js';

/**
 * @summary Menus display a list of choices on a temporary surface.
 *
 * @description
 * Menu items are the selectable choices within the menu. Menu items must
 * implement the `Menu` interface and also have the `ix-menu`
 * attribute. Additionally menu items are list items so they must also have the
 * `ix-list-item` attribute.
 *
 * Menu items can control a menu by selectively firing the `close-menu` and
 * `deselect-items` events.
 *
 * This menu item will open a sub-menu that is slotted in the `submenu` slot.
 * Additionally, the containing menu must either have `has-overflow` or
 * `positioning=fixed` set to `true` in order to display the containing menu
 * properly.
 *
 * @example
 * ```html
 * <div style="position:relative;">
 *   <button
 *       id="anchor"
 *       @click=${() => this.menuRef.value.show()}>
 *     Click to open menu
 *   </button>
 *   <!--
 *     `has-overflow` is required when using a submenu which overflows the
 *     menu's contents
 *   -->
 *   <ix-menu anchor="anchor" has-overflow ${ref(menuRef)}>
 *     <ix-menu-item headline="This is a headline"></ix-menu-item>
 *     <ix-sub-menu>
 *       <ix-menu-item
 *           slot="item"
 *           headline="this is a submenu item">
 *       </ix-menu-item>
 *       <ix-menu slot="menu">
 *         <ix-menu-item headline="This is an item inside a submenu">
 *         </ix-menu-item>
 *       </ix-menu>
 *     </ix-sub-menu>
 *   </ix-menu>
 * </div>
 * ```
 *
 * @final
 * @suppress {visibility}
 */
export class IxSubMenu extends SubMenu {
  static override styles = styles;
}
